import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { uiSfx } from "../uiSfx.js";
import type { LobbyFlow } from "./LobbyFlow.js";
import { CodeEntryModel, LOBBY_CODE_LENGTH, escapeHtml } from "../lobbyState.js";

/**
 * LOBBIES landing — HOST GAME / ENTER CODE (menu-redesign-plan §3.5).
 *
 * Two sub-states share the screen: the two-entry menu, and the code-entry
 * panel. Code entry is dual-input by design:
 *  - keyboard types into a real (visually hidden) <input> — MenuInput skips
 *    editable targets, so letters (including WASD) never double as navigation;
 *  - controller drives the on-screen keypad through handleInput (pad events
 *    bypass the editable-target rule, which is exactly what we want);
 *  - mouse/touch clicks keypad cells directly.
 * The CodeEntryModel in lobbyState.ts is the single source of truth; the
 * input element and the keypad both project it.
 */

export interface LobbyLandingDeps {
  flow: LobbyFlow;
  /** Escape/B on the menu → back to MAIN MENU. */
  onBack: () => void;
}

const OPTIONS = [
  { id: "host", label: "HOST GAME", sub: "OPEN A PRIVATE ROOM — SHARE A 5-CHAR CODE" },
  { id: "code", label: "ENTER CODE", sub: "JOIN A FRIEND'S ROOM BY CODE" },
] as const;

export class LobbyLandingScreen implements ArcadeScreen {
  private root: HTMLElement | null = null;
  private readonly code = new CodeEntryModel();
  private focusIdx = 0;
  private codeOpen = false;

  private optionEls: HTMLButtonElement[] = [];
  private codePanelEl: HTMLElement | null = null;
  private menuEl: HTMLElement | null = null;
  private boxesEl: HTMLElement | null = null;
  private padEl: HTMLElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private noticeEl: HTMLElement | null = null;
  private cellEls: HTMLButtonElement[] = [];

  constructor(private readonly deps: LobbyLandingDeps) {}

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <div class="lb-wrap lb-landing">
        <div class="lb-headline">
          <span class="lb-eyebrow">LOBBIES</span>
          <span class="lb-headrule" aria-hidden="true"></span>
        </div>
        <div class="lb-notice hidden" role="alert"></div>
        <nav class="lb-options" aria-label="Lobby options"></nav>
        <div class="lb-code-panel hidden">
          <div class="lb-code-title">ENTER ROOM CODE</div>
          <input class="lb-code-input" aria-label="Room code" maxlength="12"
                 autocomplete="off" autocapitalize="characters" spellcheck="false" />
          <div class="lb-code-boxes" aria-hidden="true"></div>
          <div class="lb-keypad" role="group" aria-label="Code keypad"></div>
          <div class="lb-code-hint">TYPE OR USE THE PAD — NO O/0 OR I/1 IN CODES</div>
        </div>
        <div class="lb-footer">
          <span class="lb-hints">↑↓←→ SELECT &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ESC / Ⓑ BACK</span>
        </div>
      </div>`;

    this.menuEl = root.querySelector(".lb-options");
    this.codePanelEl = root.querySelector(".lb-code-panel");
    this.boxesEl = root.querySelector(".lb-code-boxes");
    this.padEl = root.querySelector(".lb-keypad");
    this.inputEl = root.querySelector(".lb-code-input");
    this.noticeEl = root.querySelector(".lb-notice");

    this.optionEls = [];
    OPTIONS.forEach((opt, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "lb-option";
      btn.innerHTML = `<span class="lb-option-label">${opt.label}</span><span class="lb-option-sub">${opt.sub}</span>`;
      btn.addEventListener("mouseenter", () => this.focusTo(i, true));
      btn.addEventListener("click", () => { this.focusTo(i, false); this.activateOption(); });
      this.menuEl!.appendChild(btn);
      this.optionEls.push(btn);
    });

    // Keypad cells — one button per CodeEntryModel cell, clicks share the model.
    this.cellEls = [];
    this.code.cells().forEach((cell, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `lb-key${cell.kind !== "char" ? ` lb-key-${cell.kind}` : ""}`;
      btn.textContent = cell.kind === "char" ? cell.ch : cell.kind === "del" ? "DEL" : "GO";
      btn.addEventListener("click", () => {
        this.code.cursor = i;
        this.activateCell();
        this.inputEl?.focus();
      });
      this.padEl!.appendChild(btn);
      this.cellEls.push(btn);
    });

    // Keyboard path: the real input owns typing while the panel is open.
    this.inputEl!.addEventListener("input", () => {
      this.code.setFrom(this.inputEl!.value);
      this.syncInput();
      this.renderCode();
    });
    this.inputEl!.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        this.submitCode();
      } else if (ev.key === "Escape") {
        ev.preventDefault();
        this.closeCode();
      }
    });

    this.renderCode();
  }

  onEnter(): void {
    this.codeOpen = false;
    this.focusIdx = 0;
    const notice = this.deps.flow.takeNotice();
    this.setNotice(notice);
    this.render();
  }

  onLeave(): void {
    this.inputEl?.blur();
  }

  handleInput(action: MenuAction): boolean {
    if (this.codeOpen) return this.handleCodeInput(action);
    switch (action.type) {
      case "up":
      case "left":
        this.focusTo((this.focusIdx + OPTIONS.length - 1) % OPTIONS.length, true);
        return true;
      case "down":
      case "right":
        this.focusTo((this.focusIdx + 1) % OPTIONS.length, true);
        return true;
      case "confirm":
      case "start":
        this.activateOption();
        return true;
      case "back":
        uiSfx.play("back");
        this.deps.onBack();
        return true;
      default:
        return false;
    }
  }

  // ── Code entry sub-state ──────────────────────────────────────────────────

  private handleCodeInput(action: MenuAction): boolean {
    switch (action.type) {
      case "up":
      case "down":
      case "left":
      case "right":
        this.code.move(action.type);
        uiSfx.play("nav");
        this.renderCode();
        return true;
      case "confirm":
        this.activateCell();
        return true;
      case "start":
        this.submitCode();
        return true;
      case "back":
        this.closeCode();
        return true;
      default:
        // Letters/digits arrive as "any" from MenuInput ONLY when the hidden
        // input is not focused (e.g. after a mouse click elsewhere) — the
        // input's own events already handled the focused case.
        return true; // swallow so stray keys never leak to the menu below
    }
  }

  private activateCell(): void {
    const kind = this.code.activate();
    if (kind === "go") {
      this.submitCode();
      return;
    }
    uiSfx.play(kind === "del" ? "back" : "nav");
    this.syncInput();
    this.renderCode();
  }

  private submitCode(): void {
    if (!this.code.complete) {
      uiSfx.play("deny");
      this.setNotice(`CODES ARE ${LOBBY_CODE_LENGTH} CHARACTERS`);
      return;
    }
    uiSfx.play("confirm");
    this.setNotice(null);
    void this.deps.flow.joinByCode(this.code.value);
  }

  private openCode(): void {
    this.codeOpen = true;
    this.setNotice(null);
    this.render();
    this.inputEl?.focus();
  }

  private closeCode(): void {
    this.codeOpen = false;
    uiSfx.play("back");
    this.inputEl?.blur();
    this.render();
  }

  private activateOption(): void {
    const opt = OPTIONS[this.focusIdx]!;
    uiSfx.play("confirm");
    if (opt.id === "host") {
      this.setNotice(null);
      void this.deps.flow.host();
    } else {
      this.openCode();
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  private focusTo(idx: number, sfx: boolean): void {
    if (idx !== this.focusIdx && sfx) uiSfx.play("nav");
    this.focusIdx = idx;
    this.applyFocus();
  }

  private applyFocus(): void {
    this.optionEls.forEach((el, i) => el.classList.toggle("is-focused", !this.codeOpen && i === this.focusIdx));
  }

  private setNotice(msg: string | null): void {
    if (!this.noticeEl) return;
    if (msg) {
      this.noticeEl.innerHTML = escapeHtml(msg);
      this.noticeEl.classList.remove("hidden");
    } else {
      this.noticeEl.textContent = "";
      this.noticeEl.classList.add("hidden");
    }
  }

  private syncInput(): void {
    if (this.inputEl && this.inputEl.value.toUpperCase() !== this.code.value) {
      this.inputEl.value = this.code.value;
    }
  }

  private render(): void {
    this.menuEl?.classList.toggle("hidden", this.codeOpen);
    this.codePanelEl?.classList.toggle("hidden", !this.codeOpen);
    this.applyFocus();
    this.renderCode();
  }

  private renderCode(): void {
    if (!this.boxesEl) return;
    let boxes = "";
    for (let i = 0; i < LOBBY_CODE_LENGTH; i++) {
      const ch = this.code.value[i] ?? "";
      boxes += `<span class="lb-code-box${ch ? " filled" : ""}${i === this.code.value.length ? " next" : ""}">${escapeHtml(ch)}</span>`;
    }
    this.boxesEl.innerHTML = boxes;
    this.cellEls.forEach((el, i) => {
      el.classList.toggle("is-focused", this.codeOpen && i === this.code.cursor);
      if (el.classList.contains("lb-key-go")) el.disabled = !this.code.complete;
    });
  }
}
